import React, { useState, useEffect } from 'react';
import { getAvatarUrl } from '../../utils/imageUtils';

interface UserAvatarProps {
  name: string;
  avatar?: string | null;
  size?: number;
  className?: string;
  fallbackClassName?: string;
}

// Renders the user's profile photo with a canonicalized cache-busted URL.
// If the image fails to load (broken/legacy URL, network error) it falls back
// to the user's initials instead of showing a broken-image icon or "undefined".
const UserAvatar: React.FC<UserAvatarProps> = ({
  name,
  avatar,
  size,
  className = '',
  fallbackClassName = '',
}) => {
  const [failed, setFailed] = useState(false);
  const src = avatar ? getAvatarUrl(avatar) : '';

  useEffect(() => {
    setFailed(false);
  }, [src]);

  const imgStyle = size ? { width: size, height: size } : undefined;
  const initialsSize = size ? Math.max(size * 0.35, 11) : undefined;
  const initials = (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name}
        onError={() => setFailed(true)}
        style={imgStyle}
        className={`rounded-full object-cover bg-gray-100 shrink-0 ${className}`}
      />
    );
  }

  return (
    <div
      style={initialsSize ? { width: size, height: size, fontSize: initialsSize } : undefined}
      className={`rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 text-white font-semibold flex items-center justify-center shrink-0 ${className} ${fallbackClassName}`}
      aria-label={name}
    >
      {initials || '?'}
    </div>
  );
};

export default UserAvatar;